import { useEffect, useState, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import type { ColumnDef } from '@tanstack/react-table';
import { Plus } from 'lucide-react';
import { getUsers } from '../../services/api';
import { getErrorMessage } from '../../utils/errors';
import type { UserWithRoles } from '../../types';
import { PageHeader, Alert, Badge, Button, DataTable, type BadgeProps } from '@racing/ui';

type Variant = NonNullable<BadgeProps['variant']>;

/** Role -> badge. Semantic: administrators must stand out from drivers. */
const roleVariants: Record<string, Variant> = {
  ADMINISTRATOR: 'brand',
  TRACK_OPERATOR: 'success',
  SANCTIONING_BODY: 'info',
  DRIVER: 'neutral',
};

const UsersPage = () => {
  const [users, setUsers] = useState<UserWithRoles[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  const loadUsers = useCallback(async () => {
    try {
      setLoading(true);
      setUsers(await getUsers());
    } catch (err: unknown) {
      setError(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  const columns = useMemo<ColumnDef<UserWithRoles, any>[]>(
    () => [
      { accessorKey: 'username', header: 'Username', meta: { width: 150 } },
      { accessorKey: 'display_name', header: 'Display Name', meta: { width: 160, priority: 'sm' } },
      { accessorKey: 'email', header: 'Email', meta: { width: 200, priority: 'md' } },
      {
        accessorKey: 'auth_provider',
        header: 'Sign-in',
        cell: ({ getValue }) => (
          <Badge variant={getValue<string>() === 'entra' ? 'info' : 'neutral'}>
            {getValue<string>() === 'entra' ? 'Entra' : 'Local'}
          </Badge>
        ),
        meta: { width: 110, priority: 'md' },
      },
      {
        id: 'roles',
        header: 'Roles',
        enableSorting: false,
        cell: ({ row }) => (
          <div className="flex flex-wrap gap-1">
            {row.original.roles?.map((role) => (
              <Badge key={role.role_id} variant={roleVariants[role.role_name] ?? 'neutral'}>
                {role.role_name.replace(/_/g, ' ')}
              </Badge>
            ))}
          </div>
        ),
        meta: { width: 260 },
      },
      {
        accessorKey: 'is_active',
        header: 'Active',
        cell: ({ getValue }) =>
          getValue<boolean>() ? (
            <Badge variant="success">Active</Badge>
          ) : (
            <Badge variant="danger">Disabled</Badge>
          ),
        meta: { width: 110 },
      },
    ],
    []
  );

  return (
    <div className="flex min-w-0 flex-col">
      <PageHeader
        title="Managed Users"
        subtitle="Accounts that sign in to Control Center. Roles and assignments decide what each can reach."
        actions={
          <Button className="w-full sm:w-auto" onClick={() => navigate('/admin/users/new')}>
            <Plus /> Add Managed User
          </Button>
        }
      />

      {error && (
        <Alert variant="danger" className="mb-4" onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      <DataTable
        data={users}
        columns={columns}
        getRowId={(row) => String(row.id)}
        loading={loading}
        onRowClick={(row) => navigate(`/admin/users/${row.id}`)}
        emptyMessage="No managed users yet"
        pageSizeOptions={[10, 25, 50]}
        className="h-[calc(100dvh-16rem)] min-h-[22rem]"
      />
    </div>
  );
};

export default UsersPage;
